/**
 * Content marketplace admin hooks — catalog + tenant orders.
 *
 * Direct fetch with credentials. The catalog rarely changes so we use a
 * long staleTime; orders should refetch on tab focus.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { env } from './env';

async function fx(path: string, init: RequestInit = {}) {
  return fetch(`${env.apiBaseUrl}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      accept: 'application/json',
      'content-type': 'application/json',
      ...(init.headers ?? {}),
    },
  });
}

export const CONTENT_PACKAGES_KEY = ['admin', 'content', 'packages'] as const;
export const CONTENT_ORDERS_KEY = ['admin', 'content', 'orders'] as const;

export type ContentKind =
  | 'blog'
  | 'local_seo_city'
  | 'expert_quote'
  | 'backlink_outreach';

export interface ContentPackage {
  id: string;
  name: string;
  tagline: string;
  description: string;
  kind: ContentKind;
  cadence_per_month: number;
  price_pln_gr: number;
  price_label: string;
  scope: string[];
  sample_md: string;
  requires_tier: 'start' | 'pro' | 'pro_bundle' | 'multi';
}

export type OrderState = 'requested' | 'active' | 'paused' | 'cancelled' | 'completed';
export type OrderStage =
  | 'pending'
  | 'in_progress'
  | 'partial'
  | 'delivered'
  | 'indexed'
  | 'failed';

export interface DeliveredUrl {
  url: string;
  dr?: number;
  anchor?: string;
  indexed_at?: number;
  status?: string;
}

export interface OrderMetadata {
  landing_page_url?: string;
  anchor_texts?: string[];
  target_keywords?: string[];
  [k: string]: unknown;
}

export interface ContentOrder {
  id: string;
  tenant_id: string;
  package_id: string;
  package_name: string;
  package_kind: ContentKind | null;
  price_label: string | null;
  state: OrderState;
  stage: OrderStage;
  source: 'self_service' | 'operator' | 'stripe';
  cadence_per_month: number;
  notes: string | null;
  metadata: OrderMetadata | null;
  delivered_urls: DeliveredUrl[];
  requested_at: number;
  started_at: number | null;
  paused_at: number | null;
  cancelled_at: number | null;
  next_due_at: number | null;
  created_at: number;
  updated_at: number;
}

export function useContentPackages() {
  return useQuery({
    queryKey: CONTENT_PACKAGES_KEY,
    queryFn: async (): Promise<ContentPackage[]> => {
      const r = await fx('/api/admin/content/packages');
      if (!r.ok) throw new Error('packages_failed');
      const j = (await r.json()) as { packages: ContentPackage[] };
      return j.packages;
    },
    staleTime: 300_000,
  });
}

export function useContentOrders() {
  return useQuery({
    queryKey: CONTENT_ORDERS_KEY,
    queryFn: async (): Promise<ContentOrder[]> => {
      const r = await fx('/api/admin/content/orders');
      if (!r.ok) throw new Error('orders_failed');
      const j = (await r.json()) as { orders: ContentOrder[] };
      return j.orders;
    },
    refetchOnWindowFocus: true,
  });
}

export function useRequestOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: {
      package_id: string;
      notes?: string;
      metadata?: OrderMetadata;
    }) => {
      const r = await fx('/api/admin/content/orders', {
        method: 'POST',
        body: JSON.stringify(input),
      });
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? 'order_failed');
      }
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CONTENT_ORDERS_KEY }),
  });
}

export function useCancelOrder() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const r = await fx(`/api/admin/content/orders/${id}/cancel`, { method: 'POST' });
      if (!r.ok) throw new Error('cancel_failed');
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: CONTENT_ORDERS_KEY }),
  });
}

/* ─────────────── operator-side: deliverable review pipeline ─────────────── */

export type DeliverableState =
  | 'queued'
  | 'in_progress'
  | 'drafted'
  | 'approved'
  | 'published'
  | 'rejected'
  | 'failed';

export interface DeliverableRow {
  id: string;
  order_id: string;
  tenant_id: string;
  kind: string;
  state: DeliverableState;
  due_at: number;
  drafted_at: number | null;
  approved_at: number | null;
  published_at: number | null;
  draft_md: string | null;
  draft_url: string | null;
  published_url: string | null;
  notes: string | null;
  meta_json: string | null;
  created_at: number;
  updated_at: number;
  tenant_slug: string | null;
  tenant_legal_name: string | null;
  order_package_id: string | null;
  order_package_name: string | null;
  order_package_kind: ContentKind | null;
  order_cadence_per_month: number | null;
}

export const DELIVERABLES_KEY = ['admin', 'operator', 'content', 'deliverables'] as const;

export function useDeliverables(filter: { state?: DeliverableState | ''; tenant?: string } = {}) {
  return useQuery({
    queryKey: [...DELIVERABLES_KEY, filter.state ?? '', filter.tenant ?? ''],
    queryFn: async (): Promise<DeliverableRow[]> => {
      const params = new URLSearchParams();
      if (filter.state) params.set('state', filter.state);
      if (filter.tenant) params.set('tenant', filter.tenant);
      const r = await fx(`/api/operator/content/deliverables?${params.toString()}`);
      if (!r.ok) throw new Error('failed_to_load');
      const j = (await r.json()) as { deliverables: DeliverableRow[] };
      return j.deliverables;
    },
    refetchOnWindowFocus: true,
  });
}

export interface DeliverablePatch {
  state?: DeliverableState;
  kind?: string;
  draft_md?: string;
  draft_url?: string;
  published_url?: string;
  notes?: string;
}

export function useUpdateDeliverable() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { id: string; patch: DeliverablePatch }) => {
      const r = await fx(`/api/operator/content/deliverables/${input.id}`, {
        method: 'POST',
        body: JSON.stringify(input.patch),
      });
      if (!r.ok) {
        const j = (await r.json().catch(() => ({}))) as { error?: string };
        throw new Error(j.error ?? 'update_failed');
      }
      return r.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: DELIVERABLES_KEY }),
  });
}

